<template>
  <div class="external-data-container">
    <div>
      <h1>{{ $t('external_data.title') }}</h1>
      <input
        type="file"
        id="file"
        ref="file"
        @change="onFileSelected"
        :accept="taskMode ? 'image/*,application/pdf' : '*'"
        multiple
        :disabled="readOnly"
      />
      <h1 v-if="errorUpload">{{ $t('external_data.upload_failed') }}</h1>
    </div>
    <b-table
      :data="uploadFiles"
      :striped="true"
      :narrowed="true"
      v-if="uploadFiles && uploadFiles.length > 0"
    >
      <template slot-scope="props">
        <b-table-column :label="$t('external_data.item_uc')">
          {{ props.index + 1 }}
        </b-table-column>

        <b-table-column
          field="displayName"
          :label="$t('external_data.file_name')"
        >
          <div
            @click="onDownloadFile(props.row?.path || props.row.filePath)"
            id="file-name"
          >
            {{ props.row.displayName }}
          </div>
        </b-table-column>

        <b-table-column
          :label="$t('external_data.print_to_report')"
          v-if="!taskMode"
        >
          <b-checkbox
            v-if="canBePrinted(props.row.displayName)"
            @input="(e) => onCheckToPrint(e, props.row.uniqueFileName)"
            :value="isPrintToReport(props.row.printToReport)"
            :disabled="readOnly"
          />
          <b-checkbox
            v-else
            :disabled="true"
            :title="$t('external_data.file_type_error')"
          />
        </b-table-column>

        <b-table-column>
          <button @click="onDelete(props.index)" :disabled="readOnly">
            {{ $t('external_data.delete') }}
          </button>
        </b-table-column>
      </template>
    </b-table>
    <b-loading
      :is-full-page="true"
      :active.sync="isLoading"
      :can-cancel="false"
    ></b-loading>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import { getDocument } from 'pdfjs-dist';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'pdfjs-dist/webpack.mjs';

import { v4 } from 'uuid';
import DIJVueConfigurations from '../../../helpers/DIJVueConfigurations';
import client from '../../../config/api';
import { GET_JOB_ATTACHMENTS_COUNT } from '../../../store/jobs/actions/actionTypes';

const SUPPORTED_FILE_TYPES = ['.jpeg', '.jpg', '.bmp', '.gif', '.png', '.pdf'];
const uuidv4 = v4;

export default {
  name: 'ExternalData',
  props: {
    taskMode: {
      type: Boolean,
      default: false,
    },
    task: {
      type: Object,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      columns: [
        {
          field: 'id',
          label: 'ID',
        },
      ],
      selectedFile: null,
      file: '',
      errorUpload: false,
      files: [],
      fileType: 'file',
      isLoading: false,
    };
  },

  computed: {
    job() {
      return this.$store.state.currentJob;
    },
    site() {
      return this.$store.state.sites[0];
    },
    uploadFiles() {
      if (this.taskMode) {
        return this.files;
      }

      if (this.files.length > 0) return this.files;

      if (this.job.uploadFile) return this.job.uploadFile;
      return [];
    },
  },

  watch: {
    task(val) {
      if (val) {
        this.files = this.task.uploadFile;
      }
    },
  },

  mounted() {
    if (this.task) {
      this.files = this.task.uploadFile ? this.task.uploadFile : [];
      this.fileType = '';
    }
  },

  methods: {
    async onFileSelected(event) {
      if (this.readOnly) {
        return;
      }
      const files = Array.from(event.target.files);

      const result = await this.$store.dispatch(
        GET_JOB_ATTACHMENTS_COUNT,
        this.job._id
      );

      if (result.count + files.length > result.max) {
        this.$buefy.toast.open({
          duration: 5000,
          message: `Too many attachments. Maximum allowed is ${result.max} attachments.`,
          type: 'is-danger',
          position: 'is-top',
        });
        return;
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const file of files) {
        try {
          this.isLoading = true;
          this.errorUpload = false;
          this.selectedFile = file;
          const fileName = file.name;
          let base63ImagesArray = null;
          let fileJsonName = null;
          let filePathJson = null;

          let taskId;
          if (this.taskMode && this.task) {
            taskId = this.task._id;
          }

          // eslint-disable-next-line no-await-in-loop
          const response = await client.post(
            `/job/${this.job._id}/presign-upload-file`,
            { fileName, taskId }
          );
          const { presignedPostData, path, uniqueFileName } = response.data;
          const dijConfigurations = new DIJVueConfigurations();
          // eslint-disable-next-line no-await-in-loop
          await dijConfigurations.sendPresignedPostData(
            presignedPostData,
            file,
            file.name
          );

          if (file.type === 'application/pdf') {
            // eslint-disable-next-line no-await-in-loop
            base63ImagesArray = await this.handlePDF(file);
            fileJsonName = `${uuidv4()}_json.txt`;
            // eslint-disable-next-line no-await-in-loop
            const responsePdf = await client.post(
              `/job/${this.job._id}/presign-upload-file`,
              { fileName: fileJsonName }
            );

            const { presignedPostData: presignedPostDataJson, path: pathJson } =
              responsePdf.data;

            const blob = new Blob([JSON.stringify(base63ImagesArray)], {
              type: 'text/plain',
            });
            const fileJson = new File([blob], fileJsonName);
            filePathJson = pathJson;
            // eslint-disable-next-line no-await-in-loop
            await dijConfigurations.sendPresignedPostData(
              presignedPostDataJson,
              fileJson,
              fileJsonName
            );
          }

          const payload = {
            ...this.job,
            displayName: file.name,
            filePath: path,
            uniqueFileName,
          };

          if (filePathJson) {
            payload.filePathJson = filePathJson;
          }

          if (this.taskMode) {
            const currentTask = this.task;

            if (currentTask.externalFiles) {
              currentTask.externalFiles.push(payload);
            } else {
              currentTask.externalFiles = [payload];
            }

            // eslint-disable-next-line no-await-in-loop
            await client.post(
              `/job/${this.job._id}/task/${this.task._id}/reportfile`,
              payload
            );
            this.files.push(payload);
          } else {
            // eslint-disable-next-line no-await-in-loop
            await client.post(`/job/${this.job._id}/reportfile`, payload);
            // eslint-disable-next-line no-await-in-loop
            await this.$store.dispatch('getJob', this.job._id);
          }
        } catch (error) {
          this.errorUpload = true;
          console.error(error);
        } finally {
          this.$refs.file.value = null;
          this.isLoading = false;
        }
      }
    },

    async onDelete(index) {
      if (this.readOnly) {
        return;
      }
      if (this.taskMode) {
        await client.post(
          `/job/${this.job._id}/task/${this.task._id}/reportfile/${index}`
        );
        const file = this.files.splice(index, 1)[0];
        console.log(file);
      } else {
        await client.post(`/job/${this.job._id}/reportfile/${index}`);
        await this.$store.dispatch('getJob', this.job._id);
      }
    },

    async onDownloadFile(path) {
      const dijConfigurations = new DIJVueConfigurations();
      const response = await client.post(
        `/job/${this.job._id}/reportfile-download-url`,
        { key: path, bucketName: dijConfigurations.bucket }
      );
      this.triggerDownload(response.data);
    },

    triggerDownload(downloadLink) {
      const anchor = document.createElement('a');
      anchor.style.display = 'none';
      anchor.href = downloadLink;
      anchor.target = '_blank';

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    },

    canBePrinted(fileName) {
      const fileExt = fileName
        .substring(fileName.lastIndexOf('.'), fileName.length)
        .toLowerCase();
      return SUPPORTED_FILE_TYPES.includes(fileExt);
    },

    async onCheckToPrint(e, uniqueFileName) {
      const { uploadFile } = this.job;

      const index = uploadFile.findIndex(
        (file) => file.uniqueFileName === uniqueFileName
      );
      uploadFile[index].printToReport = e;
      const { job } = this;
      job.uploadFile = uploadFile;

      await this.$store.dispatch('updateJobSimple', job);
    },

    isPrintToReport(printToReport) {
      if (printToReport) return true;
      return false;
    },

    // Very ugly workaround untill we move the logic to use pdf on backend
    async handlePDF(file) {
      const images = [];

      // Load the PDF document using a URL created from the file
      const loadingTask = getDocument(URL.createObjectURL(file));
      const pdf = await loadingTask.promise;

      // Loop through all the pages
      for (let i = 1; i <= pdf.numPages; i++) {
        // eslint-disable-next-line no-await-in-loop
        const page = await pdf.getPage(i);

        // Define the scale and viewport for the page
        const scale = 1; // Adjust the scale if needed
        const viewport = page.getViewport({ scale });

        // Prepare canvas using PDF page dimensions
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // Render the page into the canvas context
        const renderContext = {
          canvasContext: context,
          viewport,
        };
        // eslint-disable-next-line no-await-in-loop
        await page.render(renderContext).promise;

        // Convert the canvas content to an image and push to the images array
        const src = canvas.toDataURL();
        images.push(src);
      }
      return images;
    },
  },
};
</script>

<style>
.external-data-container {
  background-color: rgb(255, 255, 255);
  padding: 10px;
  font-family: 'Siemens Roman';
  font-size: 18px;
}

#file-name {
  cursor: pointer;
  text-decoration: underline;
  color: rgb(44, 44, 249);
}
</style>
